<template>
    <div class="apa">
        <div class="introduction">
            <div class="introduction__left">
                <h1>APA格式引用快速指南</h1>

                <h2>什麼是引用?</h2>
                <p>
                  引用是指您在學術研究中對所使用資料來源的參考與說明，包括參考文獻清單和內文引用。引用在您的工作中可以起到一些關鍵作用，例如說明驗證您的研究背景，並為研究人員提供與您研究課題相關的其他資料來源。
                </p>
            </div>
            <div class="introduction__right">
                <img src="https://pstatic.wordvice.com/citation-generator/quick-guide/apa-pub-manual.jpg" alt="APA 7th Publication Manual">
            </div>
        </div>

        <div class="tables-of-contents-container" id="tables-of-contents-container">
            <div class="tables-of-contents on" id="tables-of-contents">
                <b>
                    APA 指南目錄
                </b>
                <ul class="tables-of-contents__list">
                    <li>
                        <a href="#guidelineOverview">APA 引用指南概述</a>
                    </li>
                    <li>
                        <a href="#apaReferences">APA 參考文獻</a>
                    </li>
                    <li>
                        <a href="#formatting">APA 參考文獻頁格式</a>
                    </li>
                    <li>
                        <a href="#inText">APA 內文引用</a>
                    </li>
                    <li>
                        <a href="#changes">APA 第7版的變化</a>
                    </li>
                </ul>
            </div>
        </div>

        <!-- -->
        <div class="block" id="guidelineOverview">
            <h2>APA 引用指南</h2>
            <p>
              APA（American Psychological Association，美國心理學會）格式是社會科學、行為科學、商業、護理等領域的作者和學生常用的格式。<router-link :to="{ name: 'cite-source', params: { source:'webpage', style: 'apa7' }}">Wordvice APA 參考文獻格式產生器</router-link> 可以自動免費產生準確的參考文獻格式。
            </p>

            <p>
              本引用指南介紹了《APA出版手冊第7版》（2020年）中的重要內容和準則。  Wordvice還提供APA格式(第6版)、MLA格式(第7版、第8版)、Chicago芝加哥格式(第17版)和Vancouver溫哥華格式的免費指南。
            </p>
        </div>

        <!-- -->
        <div class="block" id="apaReferences">
            <h2>APA 參考文獻</h2>
            <p>
              APA參考文獻通常包括作者、出版日期和標題等資訊。根據資料來源的類型，參考文獻的格式要遵循特定的規則，並可能需要提供有關資料來源的額外資訊。
            </p>

            <br>
            <h4>
              基本規則
            </h4>
            <p>
              即使有詳細的指南，引用資料對作者仍是一大挑戰。請參考Wordvice參考文獻格式產生器提供的範例。仔細留意每一種格式，或者直接使用<router-link :to="{ name: 'cite-source', params: { source:'webpage', style: 'apa7' }}">APA第7版參考文獻格式產生器</router-link>來瞭解具體的APA格式指南。
            </p>

          <div class="text-align-center">
            <a href="/apa-citation-generator" class="gd-btn gd-btn--try">
                使用APA格式產生器
            </a>
          </div>

            <br>
            <h4>未知資訊</h4>
            <p>
              有些情況下，尤其是對於網上的資訊來源，引用所需的資訊是未知或缺失的。請參考下表，瞭解如何引用未知來源。
            </p>

            <table class="table">
                <colgroup>
                  <col width="12%" />
                  <col width="38%" />
                  <col width="" />
                </colgroup>
                <thead>
                <tr>
                    <th>未知要素</th>
                    <th>解決辦法</th>
                    <th>參考文獻格式</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <th>作者</th>
                    <td>參考文獻條目以標題開頭</td>
                    <td>
                      <span class="highlight1">標題</span>. (日期). 資料來源.<br>
                      <span class="highlight1">Title</span>. (Date). Source.
                    </td>
                </tr>

                <tr>
                    <th>日期</th>
                    <td>採用n.d." 來表示「no date  （無日期）</td>
                    <td>
                      作者. <span class="highlight1">(n.d.)</span>. 題目. 資料來源.<br>
                      Author. <span class="highlight1">(n.d.)</span>. Title. Source.
                    </td>
                </tr>

                <tr>
                    <th>題目</th>
                    <td>用方括弧來描述引用的資訊</td>
                    <td>
                      作者. (日期). <span class="highlight1">[說明]</span>. 資料來源.<br>
                      Author. (Date). <span class="highlight1">[Description]</span>. Source.
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <!-- -->
        <div class="block" id="formatting">
            <div class="reference-list">
                <div class="reference-list__left">
                    <h2>APA 參考文獻頁(Reference Page)格式</h2>
                    <p>
                      參考文獻清單提供您在論文中引用的所有文獻資料的資訊。在參考文獻清單中，只納入你在文中引用過的文獻來源。
                    </p>

                    <div>
                      APA參考文獻頁遵循以下格式指南:
                        <ul>
                            <li>
                              在新空白頁面頂部中心位置用粗體標註“References”。
                            </li>

                            <li>
                              按字母順序列出參考文獻。
                            </li>

                            <li>
                              對於每個條目，第一行之後的所有行從左邊緣縮進1/2英寸（"懸掛縮進"）。
                            </li>

                            <li>
                              所有行，包括兩個參考文獻之間，採用兩倍行距。
                            </li>
                        </ul>
                    </div>
                </div>
                <!-- right -->
                <div class="reference-list__right">
                    <svg class="bi bi-search view-ico" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                    </svg>
                    <img class="lightbox" src="https://pstatic.wordvice.com/citation-generator/quick-guide/img_apa_reference_list_tw.png" alt="Reference List">
                </div>
            </div>

        </div>

        <!-- -->
        <div class="block" id="inText">
            <h2>APA 內文引用(in-text citations)</h2>
          <p>
            內文引用(in-text citations)是指在您的文章中的簡短引用，以標明對應的資訊來源在參考文獻清單中的位置。如果您引用或<a href="https://blog.wordvice.com.tw/影片-研究論文的改寫paraphrasing技巧apa-ama格式/" target="_blank">改寫(Paraphrase)</a>了別人的工作，您必須使用內文引用。
          </p>
          <p>
            <a href="https://blog.wordvice.com.tw/apa-格式文獻引用及範例/" target="_blank">APA的內文引用</a>的內文引用格式遵循作者-日期法(author-date)，由作者的姓氏和出版年份組成。 如果您是直接引用或借用一個資訊來源，那麼需要用縮寫 "p/pp "來納入一個頁碼。 當引用一個資訊來源的特定部分時，需要包含時間戳或頁碼等識別資訊。例如：<span class="highlight1">（Harken, 2018, pp. 145-153）</span>.
          </p>

            <br>
            <h4>
              括號夾註(Parenthetical Citation)與敘述性引用(Narrative Citation)
            </h4>
            <p>
              內文引用可以是括弧式或敘述式的。括號夾註通常跟在所引用的某一資訊來源的內容後面。 例如：
            </p>
            <ul>
                <li>
                  In 2010, everything we once knew ... <span class="highlight1">(Kim, 2008)</span>.
                </li>
            </ul>
            <p>
              敘述性引用強調的是所使用的資訊來源的作者。 例如：
            </p>
            <ul>
                <li>
                    Kim <span class="highlight1">(2008)</span> notes that …
                </li>
            </ul>

            <br>
            <h4>
              其他APA 引用規則
            </h4>
            <p>
              雖然APA文內引用的作者-日期法相當簡單直接，但其具體格式取決於資訊來源的類型和作者數量。 在作者為三個或多於三個的情況下，在列出前一個作者之後加上 "et  al." 。
            </p>

            <table class="table">
                <thead>
                <tr>
                    <th>類型</th>
                    <th>
                      括弧夾註(Parenthetical Citation)
                    </th>
                    <th>敘述性引用(Narrative Citation)</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <th>一位作者</th>
                    <td>(<span class="highlight1">Kim</span>, 2020)</td>
                    <td><span class="highlight1">Kim</span> (2020)</td>
                </tr>
                <tr>
                    <th>兩位作者</th>
                    <td>(<span class="highlight1">Tshitoyan & Dagdelen</span>, 2019)</td>
                    <td><span class="highlight1">Tshitoyan and Dagdelen</span> (2019)</td>
                </tr>
                <tr>
                    <th>三位或更多作者</th>
                    <td>(Tshitoyan <span class="highlight1">et al.</span>, 2020)</td>
                    <td>Tshitoyan <span class="highlight1">et al.</span> (2020)</td>
                </tr>
                <tr>
                    <th>機構組織</th>
                    <td>(Wordvice, 2020)</td>
                    <td>Wordvice (2020)</td>
                </tr>
                </tbody>
            </table>

            <p>
              當創建內文引用所需的任何資訊未知時，請參考下表。
            </p>

            <table class="table">
                <thead>
                <tr>
                    <th>未知要素</th>
                    <th>解決辦法</th>
                    <th>內文引用(In-text Citation)</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <th>作者</th>
                    <td>提供標題*</td>
                    <td>
                      (<i class="highlight1">資料標題</i>, 年份)<br>
                      (<i class="highlight1">Source Title</i>, year)
                    </td>
                </tr>
                <tr>
                    <th>日期</th>
                    <td>採用n.d." 來表示no date(無日期)</td>
                    <td>
                      (作者, <span class="highlight1">n.d</span>.)<br>
                      (Author, <span class="highlight1">n.d</span>.)
                    </td>
                </tr>
                <tr>
                    <th>標題</th>
                    <td>提供作者和日期</td>
                    <td>
                      (<span class="highlight1">作者, 年份</span>)或 <span class="highlight1">作者 (年份)</span><br>
                      (<span class="highlight1">Author, year</span>) or <span class="highlight1">Author (year)</span>
                    </td>
                </tr>
                </tbody>
            </table>

            <p>
              來源標題應根據來源類型，遵循一個的標題大寫規則和標題格式。另外，如果參考文獻清單中的標題不是斜體，則應放在引號內。
            </p>
        </div> <!-- end inText -->

        <div class="block" id="changes">
            <h2>APA 第7版的變化</h2>

            <p>
              APA在2019年秋季更新為第7版。 本文列出了第6版和第7版之間的主要區別。
            </p>

            <p>
                <a href="https://apastyle.apa.org/products/publication-manual-7th-edition" target="_blank">APA出版手冊第7版包含多處更新</a>。以下是對參考文獻頁和文件參考指南的一些重要修改。
            </p>

            <br>
            <h4>
              參考文獻頁格式(Reference Page)（APA第7版）
            </h4>

            <ul>
                <li>
                  當有多個作者且作者數不超過20（含20）時，提供所有作者的姓氏及名字/中間名的首字母，並在最後一個作者的姓名前用&連接。 對於有21名或更多作者的情況，在第19名和最後一名作者的名字之間使用省略號（... ）。
                </li>
                <li>
                  只有在提供了檢索日期(retrieval date)時才使用 "Retrieved from"。
                </li>
                <li>
                  在使用數位物件唯一識別碼（DOI）和URLs時，以句號結束引文條目的資料庫 / 檔案資訊，並提供電子來源的超連結。
                </li>
            </ul>

            <br>
            <h4>
              內文引用（APA第7版）
            </h4>

            <ul style="margin-bottom: 0;">
                <li>
                  有三名或三名以上作者的所有來源均使用第一作者的姓名，其後加上"et al." 但是，如果兩個來源的第一作者名字相同，則根據需要依次列出名稱，直到可區分論文為止。
                </li>
                <li>
                  引述研究參與者時應採用與一般引述相同的格式，以表明引文來自源檔中的研究參與者。
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: "QuickGuideAPA",
    metaInfo() {
        return {
            title : `APA引用格式快速指南 | ${this.$t('Brand Name')}`,
            meta: [
                {
                    name: 'description',
                    content: 'APA常被用於心理學、教育學和社會學科領域。那麼，APA格式如何使用？ APA格式有哪些要求呢？ Wordvice就來帶大家全面了解一下APA引用格式的正確使用方法！',
                }
            ],
            link: [
                {rel: 'canonical', href: `${window.location.origin}/apa-citation-generator`}
            ]
        }
    },
    mounted() {
        import('@/components/quickguide/common.js');
    }
}
</script>



// WEBPACK FOOTER //
// src/components/quickguide/zh_TW/apa.vue